import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Send, X, MessageCircle, User, Store, Car, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

// Chat interface for order communication
export function OrderChat({ orderId, isOpen, onClose, participants = {} }) {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (isOpen && orderId) {
      fetchMessages();
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isOpen, orderId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/chat/messages/${orderId}`);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    try {
      const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
      wsRef.current = new WebSocket(`${wsUrl}/ws/chat/${orderId}`);
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          setMessages(prev => [...prev, data.message]);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      await api.post('/chat/messages', {
        order_id: orderId,
        message: newMessage.trim(),
      });
      
      // Add message optimistically
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        order_id: orderId,
        sender_id: user.id,
        sender_name: user.name,
        sender_role: user.role,
        message: newMessage.trim(),
        created_at: new Date().toISOString(),
      }]);
      
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const getSenderIcon = (role) => {
    switch (role) {
      case 'customer':
        return <User className="w-4 h-4" />;
      case 'restaurant_admin':
        return <Store className="w-4 h-4" />;
      case 'driver':
        return <Car className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getSenderColor = (role) => {
    switch (role) {
      case 'customer':
        return 'bg-blue-500';
      case 'restaurant_admin':
        return 'bg-orange-500';
      case 'driver':
        return 'bg-green-500';
      default:
        return 'bg-slate-500';
    }
  };

  const isOwnMessage = (message) => message.sender_id === user?.id;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl w-full max-w-md h-[600px] flex flex-col overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-5 h-5" />
            <div>
              <h3 className="font-semibold">Order Chat</h3>
              <p className="text-xs text-orange-100">Order #{orderId?.slice(0, 8)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Participants */}
        <div className="px-4 py-2 bg-slate-50 border-b flex gap-2 text-xs">
          {participants.customer && (
            <span className="flex items-center gap-1 text-blue-600">
              <User className="w-3 h-3" /> Customer
            </span>
          )}
          {participants.restaurant && (
            <span className="flex items-center gap-1 text-orange-600">
              <Store className="w-3 h-3" /> Restaurant
            </span>
          )}
          {participants.driver && (
            <span className="flex items-center gap-1 text-green-600">
              <Car className="w-3 h-3" /> Driver
            </span>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No messages yet</p>
              <p className="text-sm">Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex items-end gap-2 ${isOwnMessage(message) ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-7 h-7 rounded-full ${getSenderColor(message.sender_role)} flex items-center justify-center text-white flex-shrink-0`}>
                  {getSenderIcon(message.sender_role)}
                </div>
                <div className={`max-w-[75%] ${isOwnMessage(message) ? 'items-end' : 'items-start'}`}>
                  <div className={`px-3 py-2 rounded-2xl ${
                    isOwnMessage(message)
                      ? 'bg-orange-500 text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-900 rounded-bl-md'
                  }`}>
                    <p className="text-sm">{message.message || message.content}</p>
                  </div>
                  <p className={`text-xs text-slate-400 mt-1 ${isOwnMessage(message) ? 'text-right' : ''}`}>
                    {message.sender_name} • {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1"
              disabled={sending}
            />
            <Button 
              type="submit" 
              disabled={!newMessage.trim() || sending}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Floating chat button
export function ChatButton({ orderId, onClick, unreadCount = 0 }) {
  return (
    <button
      onClick={onClick}
      className="relative inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors shadow-lg"
    >
      <MessageCircle className="w-5 h-5" />
      <span className="text-sm font-medium">Chat</span>
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
}

export default OrderChat;
